import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { KIND_LABEL, timelineItems } from "@/components/pipeline/LeadTimeline";
import { prefetchLead } from "@/components/pipeline/lead/leadCache";
import { toneOfKind } from "@/components/pipeline/lead/LeadHistory";
import { frDateTime } from "@/components/pipeline/utils";
import { fine } from "@/themes/rapport/motion";

const strip = (t) => (t || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const W = 330;

// Aperçu au survol d'une carte : un seul écouteur pour tout le Pipeline, fiche lue après 120 ms d'arrêt, aperçu affiché à 450 ms
export const HoverPreview = ({ rootRef, disabled }) => {
  const [st, setSt] = useState(null);
  // Lecture anticipée dès l'appui (souris ou doigt) : la fiche est souvent déjà en mémoire au moment du clic
  useEffect(() => {
    const root = rootRef.current;
    if (!root || disabled) return undefined;
    const down = (e) => { const id = e.target.closest?.("[data-lead-id]")?.dataset.leadId; if (id) prefetchLead(id).catch(() => {}); };
    root.addEventListener("pointerdown", down);
    return () => root.removeEventListener("pointerdown", down);
  }, [rootRef, disabled]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root || disabled || !fine.matches) return undefined;
    let timer;
    let early;
    let cur = null;
    const hide = () => { cur = null; clearTimeout(timer); clearTimeout(early); setSt(null); };
    const over = (e) => {
      const card = e.target.closest?.("[data-lead-id]");
      if (!card || card === cur || document.body.classList.contains("pl-dragging")) return;
      hide();
      cur = card;
      early = setTimeout(() => prefetchLead(card.dataset.leadId).catch(() => {}), 120);
      timer = setTimeout(async () => {
        const data = await prefetchLead(card.dataset.leadId).catch(() => null);
        if (cur === card && data) setSt({ rect: card.getBoundingClientRect(), data });
      }, 450);
    };
    const out = (e) => { if (cur && !cur.contains(e.relatedTarget)) hide(); };
    root.addEventListener("mouseover", over);
    window.addEventListener("pl-drag-start", hide);
    root.addEventListener("mouseout", out);
    root.addEventListener("mousedown", hide);
    root.addEventListener("dragstart", hide);
    window.addEventListener("scroll", hide, true);
    return () => {
      hide();
      root.removeEventListener("mouseover", over);
      window.removeEventListener("pl-drag-start", hide);
      root.removeEventListener("mouseout", out);
      root.removeEventListener("mousedown", hide);
      root.removeEventListener("dragstart", hide);
      window.removeEventListener("scroll", hide, true);
    };
  }, [rootRef, disabled]);
  if (!st || disabled) return null;
  const { rect, data } = st;
  const left = rect.right + 12 + W < window.innerWidth ? rect.right + 12 : Math.max(8, rect.left - W - 12);
  const top = Math.max(8, Math.min(rect.top, window.innerHeight - 320));
  const items = timelineItems(data.comments, data.communications).slice(0, 3);
  return createPortal(
    <div className="pop ldp-peek" style={{ left, top, width: W }} role="tooltip" data-testid="pipeline-hover-preview">
      <strong className="ldp-peek__title">{data.lead.title}</strong>
      <small className="ldp-peek__sub">{data.lead.step} · {data.owner_name || "Non attribuée"}</small>
      {items.length ? (
        <ul className="ldp-peek__list">
          {items.map((i) => (
            <li key={i.id}>
              <span className={`ldp-peek__dot tone--${toneOfKind(i.kind) === "plain" ? "muted" : toneOfKind(i.kind)}`} />
              <div><b>{KIND_LABEL[i.kind] || "Échange"}</b> · <span>{frDateTime(i.at)}</span><p>{strip(i.ai_summary || i.excerpt) || i.title}</p></div>
            </li>
          ))}
        </ul>
      ) : <p className="ldp-peek__empty">Aucun échange pour l'instant.</p>}
    </div>,
    document.body,
  );
};
